import { useCallback } from "react";
import { $api } from "@babackapp/private-api-client/generated/client";
import { injectEmbedScript } from "./inject-embed-script";

type UseOpenReturnWidgetOptions = {
  /** Preview the V3 portal even when the shop has not opted into it. */
  forceNewWidget?: boolean;
  /** Language to force the portal to, e.g. the shop's default. */
  lang?: string;
};

type ReturnWidget = {
  open: () => void;
  setSource: (source: string) => void;
};

/**
 * Entry URL of a shop's portal. The `reset` search param sits before the hash
 * so the iframe reloads the document: the portal only ever navigates its hash,
 * and a hash-only source change leaves it on the page the previous session
 * ended on. `advanced` mirrors the `data-advanced` the office injects with.
 * `lang` forces the shop's default language: opened from the office there is
 * no order or storefront locale to resolve from, so the portal would otherwise
 * fall back to the admin's browser language (see BBS-430).
 */
function buildEntrySource(providerId: string, lang?: string) {
  const langParam = lang ? `&lang=${lang}` : "";
  return `${window.location.origin}/widget.html?reset=${Date.now()}#/return-widget/${providerId}?advanced=true${langParam}`;
}

export function useOpenReturnWidget(
  shopifyUrl: string,
  providerId: string,
  { forceNewWidget = false, lang }: UseOpenReturnWidgetOptions = {},
) {
  const { refetch } = $api.useQuery(
    "get",
    "/embed-html",
    {
      params: { query: { shopifyUrl, autoOpen: true } },
      enabled: false,
    },
  );

  return useCallback(async () => {
    const w = window as unknown as Record<string, unknown>;
    const widget = w.babackReturnWidget as ReturnWidget | undefined;

    // Re-injecting the embed script for another shop yields the first shop's
    // portal: createWidget registers the custom element once, and the class it
    // registers closes over the config of that first call. So an existing
    // widget is retargeted through its source, which carries the shop.
    if (widget) {
      widget.setSource(buildEntrySource(providerId, lang));
      widget.open();
      return;
    }

    const { data } = await refetch();
    if (!data?.shopId) return;
    injectEmbedScript(data, data.shopId, {
      noOverlayClose: false,
      forceNewWidget,
      lang,
    });
  }, [refetch, providerId, forceNewWidget, lang]);
}
